import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { showToast } from '../layout/ToastContainer';

function formatDate(iso) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

export default function Submitters() {
  const [submitters, setSubmitters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [csvLoading, setCsvLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/results/submitters');
      setSubmitters(data.submitters);
    } catch {
      showToast('Failed to load submitters', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = submitters.filter(r =>
    !search || [r.name, r.email, r.phone, r.ip, r.isp, r.browser, r.os]
      .some(v => v?.toLowerCase().includes(search.toLowerCase()))
  );

  function toggleCheck(id) {
    setCheckedIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  }

  function toggleAll() {
    if (checkedIds.size === filtered.length && filtered.length > 0) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(filtered.map(r => r.id)));
    }
  }

  async function removeSelected() {
    setDeleting(true);
    try {
      const ids = Array.from(checkedIds);
      await Promise.all(ids.map(id => api.patch(`/results/${id}`, { hiddenFromSubmitters: true })));
      const n = ids.length;
      setSubmitters(prev => prev.filter(r => !checkedIds.has(r.id)));
      setCheckedIds(new Set());
      setConfirmDelete(false);
      showToast(`${n} submitter${n !== 1 ? 's' : ''} removed from list`, 'success');
    } catch {
      showToast('Remove failed', 'error');
    } finally {
      setDeleting(false);
    }
  }

  async function downloadCSV() {
    setCsvLoading(true);
    try {
      const token = localStorage.getItem('speedtest_token');
      const ids = checkedIds.size > 0 ? Array.from(checkedIds) : [];
      const response = await fetch('/api/results/submitters/csv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids })
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = response.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      a.download = match ? match[1] : 'feedback-submitters.csv';
      a.click();
      URL.revokeObjectURL(url);
      const n = ids.length || submitters.length;
      showToast(`Exported ${n} record${n !== 1 ? 's' : ''}`, 'success');
    } catch {
      showToast('CSV export failed', 'error');
    } finally {
      setCsvLoading(false);
    }
  }

  const allChecked = filtered.length > 0 && checkedIds.size === filtered.length;
  const someChecked = checkedIds.size > 0;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '0.05em' }}>
            Submitters
          </h1>
          <div style={{ color: '#9aa3b0', fontSize: 13, marginTop: 4 }}>
            {submitters.length} total submitter{submitters.length !== 1 ? 's' : ''} (all time, including archived)
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}
            style={{ color: '#d0d8e4', borderColor: '#3e4a5a' }}>
            {loading ? <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> : '↻ Refresh'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={downloadCSV} disabled={csvLoading || submitters.length === 0}>
            {csvLoading ? 'Exporting...' : someChecked ? `↓ Export ${checkedIds.size} Selected` : '↓ Export All CSV'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          className="input-field"
          placeholder="Search by name, email, IP, browser..."
          value={search}
          onChange={e => { setSearch(e.target.value); setCheckedIds(new Set()); }}
          style={{ maxWidth: 360 }}
        />
        {someChecked && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{checkedIds.size} selected</span>
            <button className="btn btn-danger btn-sm" onClick={() => setConfirmDelete(true)}>
              Remove from List
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setCheckedIds(new Set())}
              style={{ color: '#9aa3b0', borderColor: '#3e4a5a' }}>Clear</button>
          </div>
        )}
      </div>

      <div className="card" style={{ overflow: 'auto' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <div className="spinner spinner-lg" />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
            {search ? 'No submitters match your search.' : 'No submissions yet.'}
          </div>
        ) : (
          <table className="data-table" style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th style={{ width: 36, textAlign: 'center' }}>
                  <input type="checkbox" checked={allChecked} onChange={toggleAll}
                    style={{ cursor: 'pointer', accentColor: 'var(--accent-orange)' }} />
                </th>
                <th>Date</th>
                <th>Type</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th style={{ textAlign: 'right' }}>Down↓</th>
                <th style={{ textAlign: 'right' }}>Up↑</th>
                <th>ISP</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const isGeneral = (r.type || 'issue_report') === 'general_feedback';
                return (
                  <tr key={r.id} style={{ opacity: r.archived ? 0.55 : 1 }}>
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={checkedIds.has(r.id)} onChange={() => toggleCheck(r.id)}
                        style={{ cursor: 'pointer', accentColor: 'var(--accent-orange)' }} />
                    </td>
                    <td style={{ color: '#b8c0cc', whiteSpace: 'nowrap' }}>{formatDate(r.createdAt)}</td>
                    <td>
                      <span style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                        color: isGeneral ? '#7db8f7' : '#f47b20',
                        background: isGeneral ? 'rgba(100,160,255,0.12)' : 'rgba(244,123,32,0.12)',
                        padding: '2px 7px', borderRadius: 10, display: 'inline-block', whiteSpace: 'nowrap'
                      }}>
                        {isGeneral ? 'GENERAL' : 'ISSUE'}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{r.name}</td>
                    <td style={{ color: '#c8d0dc', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.email}</td>
                    <td style={{ color: '#9aa3b0' }}>{r.phone || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: isGeneral ? '#6e7789' : 'var(--accent-orange)', fontWeight: 600 }}>
                      {isGeneral ? <span style={{ color: 'var(--text-muted)' }}>—</span> : <>{r.download.toFixed(1)} <span style={{ color: '#9aa3b0', fontWeight: 400 }}>Mbps</span></>}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: isGeneral ? '#6e7789' : 'var(--accent-orange)', fontWeight: 600 }}>
                      {isGeneral ? <span style={{ color: 'var(--text-muted)' }}>—</span> : <>{r.upload.toFixed(1)} <span style={{ color: '#9aa3b0', fontWeight: 400 }}>Mbps</span></>}
                    </td>
                    <td style={{ color: '#c8d0dc' }}>{r.isp || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td>
                      {r.archived ? (
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: '#9aa3b0', background: 'rgba(154,163,176,0.12)', padding: '2px 7px', borderRadius: 10, display: 'inline-block' }}>
                          ARCHIVED
                        </span>
                      ) : (
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: '#00c88c', background: 'rgba(0,200,140,0.12)', padding: '2px 7px', borderRadius: 10, display: 'inline-block' }}>
                          ACTIVE
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {confirmDelete && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setConfirmDelete(false)}>
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header"><h2>Remove from Submitters</h2></div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6 }}>
                Remove {checkedIds.size} submitter{checkedIds.size !== 1 ? 's' : ''} from this list? Their submission data will be kept in the Submissions tab.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="btn btn-danger btn-sm" onClick={removeSelected} disabled={deleting}>
                {deleting ? 'Removing...' : 'Remove from List'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
